import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject, PointerEvent as ReactPointerEvent } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import { NON_REORDERABLE_COLUMN_IDS } from './buildDagColumnDefs'
import {
  useColumnFreezeStore,
  usePinnedColumnIds,
} from '@/stores/useColumnFreezeStore'
import type { RowData } from './useProductsTableState'

interface FreezeColumnDividerProps {
  gridRef: RefObject<AgGridReact<RowData> | null>
  containerRef: RefObject<HTMLDivElement | null>
  freezeScope: string
}

interface HeaderCellBoundary {
  colId: string
  right: number
}

function getHeaderCellBoundaries(
  container: HTMLDivElement
): HeaderCellBoundary[] {
  const containerRect = container.getBoundingClientRect()
  const cells = container.querySelectorAll<HTMLElement>(
    '.ag-header-cell[col-id]'
  )
  const boundaries: HeaderCellBoundary[] = []
  cells.forEach(cell => {
    const colId = cell.getAttribute('col-id')
    if (!colId) return
    const rect = cell.getBoundingClientRect()
    // Skip pinned-right cells (only 'actions' today) — the divider only
    // ever moves the left-freeze boundary.
    if (cell.closest('.ag-pinned-right-header')) return
    boundaries.push({ colId, right: rect.right - containerRect.left })
  })
  return boundaries
}

/**
 * A draggable Excel/Sheets-style freeze-line divider rendered over the DAG
 * table. Dragging it snaps to the nearest column boundary and freezes
 * (pins left) every dynamic column up to that point, mirroring Excel's
 * "freeze first N columns" mental model. Precise/non-contiguous pinning is
 * handled separately via the per-column control in ColumnSelectorPopover.
 */
export function FreezeColumnDivider({
  gridRef,
  containerRef,
  freezeScope,
}: FreezeColumnDividerProps) {
  const setPinned = useColumnFreezeStore(s => s.setPinned)
  const pinnedColumnIds = usePinnedColumnIds(freezeScope)
  const [restLeft, setRestLeft] = useState<number | null>(null)
  const [dragLeft, setDragLeft] = useState<number | null>(null)
  const dragStateRef = useRef<{
    onMove: (e: PointerEvent) => void
    onUp: (e: PointerEvent) => void
  } | null>(null)

  const recomputeRestLeft = useCallback(() => {
    const api = gridRef.current?.api
    if (!api) return
    const width = api
      .getDisplayedLeftColumns()
      .reduce((sum, col) => sum + col.getActualWidth(), 0)
    setRestLeft(width)
  }, [gridRef])

  // Retries on every animation frame until the grid api is attached —
  // AgGridReact assigns its ref during its own mount, which isn't
  // guaranteed to have happened yet when this sibling's effect first runs.
  useEffect(() => {
    let cancelled = false
    let rafId: number
    let cleanupListeners: (() => void) | null = null

    const tryInit = () => {
      if (cancelled) return
      const api = gridRef.current?.api
      if (!api) {
        rafId = requestAnimationFrame(tryInit)
        return
      }
      recomputeRestLeft()
      const handler = () => recomputeRestLeft()
      api.addEventListener('columnResized', handler)
      api.addEventListener('gridSizeChanged', handler)
      api.addEventListener('columnPinned', handler)
      api.addEventListener('columnMoved', handler)
      api.addEventListener('firstDataRendered', handler)
      cleanupListeners = () => {
        if (api.isDestroyed?.()) return
        api.removeEventListener('columnResized', handler)
        api.removeEventListener('gridSizeChanged', handler)
        api.removeEventListener('columnPinned', handler)
        api.removeEventListener('columnMoved', handler)
        api.removeEventListener('firstDataRendered', handler)
      }
    }
    tryInit()

    return () => {
      cancelled = true
      if (rafId) cancelAnimationFrame(rafId)
      cleanupListeners?.()
    }
  }, [recomputeRestLeft, pinnedColumnIds, gridRef])

  useEffect(
    () => () => {
      if (dragStateRef.current) {
        window.removeEventListener('pointermove', dragStateRef.current.onMove)
        window.removeEventListener('pointerup', dragStateRef.current.onUp)
      }
    },
    []
  )

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const container = containerRef.current
    if (!container) return

    const onMove = (moveEvent: PointerEvent) => {
      const boundaries = getHeaderCellBoundaries(container)
      if (boundaries.length === 0) return
      const containerRect = container.getBoundingClientRect()
      const x = moveEvent.clientX - containerRect.left
      let closest = boundaries[0]
      let closestDist = Math.abs(boundaries[0].right - x)
      for (const b of boundaries) {
        const dist = Math.abs(b.right - x)
        if (dist < closestDist) {
          closest = b
          closestDist = dist
        }
      }
      setDragLeft(closest.right)
    }

    const onUp = (upEvent: PointerEvent) => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      dragStateRef.current = null

      const api = gridRef.current?.api
      const containerEl = containerRef.current
      setDragLeft(null)
      if (!api || !containerEl) return

      const boundaries = getHeaderCellBoundaries(containerEl)
      if (boundaries.length === 0) return
      const containerRect = containerEl.getBoundingClientRect()
      const x = upEvent.clientX - containerRect.left
      let targetColId = boundaries[0].colId
      let closestDist = Math.abs(boundaries[0].right - x)
      for (const b of boundaries) {
        const dist = Math.abs(b.right - x)
        if (dist < closestDist) {
          targetColId = b.colId
          closestDist = dist
        }
      }

      const displayed = api.getAllDisplayedColumns()
      const targetIndex = displayed.findIndex(
        col => col.getColId() === targetColId
      )
      if (targetIndex === -1) return

      const toPin: string[] = []
      const toUnpin: string[] = []
      displayed.forEach((col, index) => {
        const colId = col.getColId()
        if (NON_REORDERABLE_COLUMN_IDS.has(colId)) return
        if (index <= targetIndex) {
          toPin.push(colId)
        } else {
          toUnpin.push(colId)
        }
      })

      setPinned(freezeScope, toUnpin, false)
      setPinned(freezeScope, toPin, true)
      recomputeRestLeft()
    }

    dragStateRef.current = { onMove, onUp }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  if (restLeft === null) return null

  const left = dragLeft ?? restLeft

  return (
    <div
      className="freeze-column-divider"
      style={{ left }}
      onPointerDown={handlePointerDown}
      title="Drag to freeze columns"
    />
  )
}
